import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatCurrencyOrEmptyValue } from 'support/helpers/currency/currency';
import { InvoiceVerificationDTO } from 'support/types';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';

import { Footer } from './components/Footer';

type InvoiceVerificationDetails = Required<InvoiceVerificationDTO>['payload'];

type SummaryItemData = {
  label: string;
  actual: string | number;
  expected: string | number;
};

type SummaryRowProps = {
  item: SummaryItemData;
};

export const InvoiceVerificationDetailsSummary = () => {
  const data = useWatch<InvoiceVerificationDetails>();
  const { service } = useErrorContext();
  const numberOfErrors = service.useNumberOfActiveErrors();
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white text-xs">
      <table className="w-full">
        <Headers />
        <SummaryData data={data.summary} currency={data.currency} />
        <Footer data={data.summary} currency={data.currency} showDifferenceIndicator={numberOfErrors > 0} />
      </table>
    </div>
  );
};

const Headers = () => {
  const { t } = useTranslation();

  return (
    <thead>
      <tr>
        <th className="pb-4 pl-6 pt-6 text-left text-gray-900"></th>
        <th className="pb-4 pt-6 text-right text-gray-900">
          <div className="flex items-center justify-end">
            {t('invoiceVerification:detailsPage.summaryData.headers.expected.label')}
            <Tooltip>
              <TooltipTrigger>
                <InformationCircleIcon className="ml-1 size-4 text-gray-400 hover:text-procuros-green-500" />
              </TooltipTrigger>
              <TooltipContent>
                {t('invoiceVerification:detailsPage.summaryData.headers.expected.tooltip')}
              </TooltipContent>
            </Tooltip>
          </div>
        </th>
        <th className="pb-4 pr-6 pt-6 text-right text-gray-900">
          <div className="flex items-center justify-end">
            {t('invoiceVerification:detailsPage.summaryData.headers.actual.label')}
            <Tooltip>
              <TooltipTrigger>
                <InformationCircleIcon className="ml-1 size-4 text-gray-400 hover:text-procuros-green-500" />
              </TooltipTrigger>
              <TooltipContent>{t('invoiceVerification:detailsPage.summaryData.headers.actual.tooltip')}</TooltipContent>
            </Tooltip>
          </div>
        </th>
      </tr>
    </thead>
  );
};

const SummaryRow = ({ item }: SummaryRowProps) => (
  <tr>
    <td className="py-2 pl-6 text-gray-500">{item.label}</td>
    <td className="py-2 text-right tabular-nums text-gray-900">{item.expected}</td>
    <td className="py-2 pr-6 text-right tabular-nums text-gray-900">{item.actual}</td>
  </tr>
);

const SummaryData = ({
  data,
  currency,
}: {
  data: Partial<InvoiceVerificationDetails['summary']>;
  currency: InvoiceVerificationDetails['currency'];
}) => {
  const { t } = useTranslation();
  const summaryData: Array<SummaryItemData> = useMemo(
    () => [
      {
        label: t('invoiceVerification:detailsPage.summaryData.totalQuantity.label'),
        actual: data?.total_quantity?.actual ?? EMPTY_FIELD,
        expected: data?.total_quantity?.expected ?? EMPTY_FIELD,
      },
      {
        label: t('invoiceVerification:detailsPage.summaryData.subtotal.label'),
        actual: formatCurrencyOrEmptyValue(data?.subtotal?.actual, currency),
        expected: formatCurrencyOrEmptyValue(data?.subtotal?.expected, currency),
      },
      {
        label: t('invoiceVerification:detailsPage.summaryData.allowances.label'),
        actual: formatCurrencyOrEmptyValue(data?.allowances?.actual, currency, (value) => value * -1),
        expected: formatCurrencyOrEmptyValue(data?.allowances?.expected, currency, (value) => value * -1),
      },
      {
        label: t('invoiceVerification:detailsPage.summaryData.charges.label'),
        actual: formatCurrencyOrEmptyValue(data?.charges?.actual, currency),
        expected: formatCurrencyOrEmptyValue(data?.charges?.expected, currency),
      },
      {
        label: t('invoiceVerification:detailsPage.summaryData.taxes.label'),
        actual: formatCurrencyOrEmptyValue(data?.taxes?.actual, currency),
        expected: formatCurrencyOrEmptyValue(data?.taxes?.expected, currency),
      },
    ],
    [data, t, currency],
  );

  return (
    <tbody>
      {summaryData.map((item, index) => (
        <SummaryRow key={`summary-${index}-${item.label}`} item={item} />
      ))}
    </tbody>
  );
};
